"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.remirrorNodeToSquizNode = exports.resolveNodeTag = void 0;
const remirror_1 = require("remirror");
const undefinedIfEmpty_1 = require("../../undefinedIfEmpty");
const Extensions_1 = require("../../../Extensions/Extensions");
const resolveNodeTag = (node) => {
    if (node.type.name === Extensions_1.NodeName.Text) {
        return 'span';
    }
    if (node.type.spec?.toDOM) {
        const domNode = node.type.spec.toDOM(node);
        if (domNode instanceof window.Node) {
            return domNode.nodeName.toLowerCase();
        }
        if (typeof domNode === 'object' && 'dom' in domNode && domNode.dom instanceof window.Node) {
            return domNode.dom.nodeName.toLowerCase();
        }
        if (domNode instanceof Array) {
            // [ tag, attributes, ...children ]
            return domNode[0].toLowerCase();
        }
    }
    throw new Error('Unexpected Remirror node encountered, cannot resolve tag.');
};
exports.resolveNodeTag = resolveNodeTag;
const resolveFormattingOptions = (node) => {
    const formattingOptions = {};
    const textAlignment = node.attrs.nodeTextAlignment;
    const textAlignmentMap = {
        none: undefined,
        left: 'left',
        right: 'right',
        center: 'center',
        start: 'left',
        end: 'right',
        justify: 'justify',
    };
    if (textAlignment && textAlignmentMap[textAlignment]) {
        formattingOptions.alignment = textAlignmentMap[textAlignment];
    }
    return formattingOptions;
};
const resolveFontOptions = (node) => {
    const fontOptions = {};
    node.marks.forEach((mark) => {
        switch (mark.type.name) {
            case 'underline':
                fontOptions.underline = true;
                break;
        }
    });
    return fontOptions;
};
const transformAttributes = (attributes, nodeType) => {
    const transformed = {};
    Object.keys(attributes).forEach((key) => {
        // Component service requires attributes to be a string, cast as needed.
        if (typeof attributes[key] === 'string' || typeof attributes[key] === 'number') {
            transformed[key] = String(attributes[key]);
        }
    });
    // We assign an attribute here for table controller cells as we need to differentiate
    // between them and regular table headers (th)
    if (nodeType === Extensions_1.NodeName.TableControllerCell) {
        transformed[nodeType] = 'true';
    }
    // Another check here for more specific attributes for tables (column widths)
    if (nodeType === Extensions_1.NodeName.TableControllerCell || nodeType === Extensions_1.NodeName.tableCell) {
        if (Array.isArray(attributes.colwidth) && attributes.colwidth[0]) {
            transformed.colwidth = String(attributes.colwidth[0]);
        }
    }
    return transformed;
};
const transformFragment = (fragment) => {
    const transformed = [];
    fragment.forEach((child) => {
        transformed.push(transformNode(child));
    });
    return transformed;
};
const transformNode = (node) => {
    const formattingOptions = (0, undefinedIfEmpty_1.undefinedIfEmpty)(resolveFormattingOptions(node));
    const font = (0, undefinedIfEmpty_1.undefinedIfEmpty)(resolveFontOptions(node));
    let attributes;
    if (node.type.name === Extensions_1.NodeName.Image ||
        node.type.name === Extensions_1.NodeName.CodeBlock ||
        node.type.name === Extensions_1.NodeName.TableControllerCell ||
        node.type.name === Extensions_1.NodeName.tableCell) {
        attributes = transformAttributes(node.attrs, node.type.name);
    }
    let transformedNode = { type: 'text', value: node.text || '' };
    // Squiz "text" nodes can't have formatting/font options but Remirror "text" nodes can.
    // If the node has formatting options wrap in a tag.
    // If the node isn't a text type assume it is a tag type and wrap in a tag.
    // If we pick the wrong tag here it will be corrected later as part of looping through the
    // non-font marks.
    if (node.type.name !== Extensions_1.NodeName.Text || attributes || formattingOptions || font) {
        transformedNode = {
            type: 'tag',
            tag: (0, exports.resolveNodeTag)(node),
            children: node.type.name === Extensions_1.NodeName.Text ? [transformedNode] : transformFragment(node.content),
            attributes,
            formattingOptions,
            font,
        };
    }
    if (node.type.name === Extensions_1.NodeName.AssetImage) {
        transformedNode = {
            type: 'matrix-image',
            matrixAssetId: node.attrs.matrixAssetId,
            matrixIdentifier: node.attrs.matrixIdentifier,
            matrixDomain: node.attrs.matrixDomain,
        };
    }
    if (node.type.name === Extensions_1.NodeName.DAMImage) {
        transformedNode = {
            type: 'dam-image',
            damObjectId: node.attrs.damObjectId,
            damSystemIdentifier: node.attrs.damSystemIdentifier,
            damSystemType: node.attrs.damSystemType,
            damAdditional: node.attrs.damAdditional ? JSON.parse(node.attrs.damAdditional) : undefined,
        };
    }
    node.marks.forEach((mark) => {
        transformedNode = transformMark(mark, transformedNode);
    });
    if (node.type.name === Extensions_1.NodeName.Unsupported) {
        const unsupportedNode = node.attrs?.originalNode;
        return { ...unsupportedNode };
    }
    return transformedNode;
};
/**
 * Merges 2 nodes together if they are compatible without losing any important details.
 * Otherwise will wrap the node.
 *
 * @param {FormattedNode} node
 * @param {FormattedNodeWithChildren} wrappingNode
 *
 * @return {FormattedNode}
 */
const wrapNodeIfNeeded = (node, wrappingNode, copyFont = true) => {
    const wrappingNodeChildren = wrappingNode.children || [];
    if (node.type === 'tag' && wrappingNode.type === 'tag' && (node.tag === 'span' || node.tag === wrappingNode.tag)) {
        const nodeChildren = node.children || [];
        // if the node we are wrapping with is a DOM node, and the node being wrapped is
        // a plain looking DOM node merge the 2 nodes.
        return {
            ...node,
            ...wrappingNode,
            formattingOptions: (0, undefinedIfEmpty_1.undefinedIfEmpty)({
                ...node.formattingOptions,
                ...wrappingNode.formattingOptions,
            }),
            attributes: (0, undefinedIfEmpty_1.undefinedIfEmpty)({
                ...node.attributes,
                ...wrappingNode.attributes,
            }),
            font: (0, undefinedIfEmpty_1.undefinedIfEmpty)(copyFont
                ? {
                    ...node.font,
                    ...wrappingNode.font,
                }
                : {}),
            children: [...nodeChildren, ...wrappingNodeChildren],
        };
    }
    // if the node we are wrapping or the wrapping nodes are not compatible merge them.
    return {
        ...wrappingNode,
        children: [node, ...wrappingNodeChildren],
    };
};
const transformMark = (mark, node) => {
    switch (mark.type.name) {
        case 'bold':
            return wrapNodeIfNeeded(node, {
                type: 'tag',
                tag: 'strong',
                children: [],
            }, false);
        case 'italic':
            return wrapNodeIfNeeded(node, {
                type: 'tag',
                tag: 'em',
                children: [],
            }, false);
        case 'underline':
            return wrapNodeIfNeeded(node, {
                type: 'tag',
                tag: 'span',
                children: [],
                font: {
                    underline: mark.type.name === 'underline',
                },
            });
        case 'link':
            return wrapNodeIfNeeded(node, {
                type: 'tag',
                tag: 'a',
                attributes: transformAttributes(mark.attrs),
                children: [],
            }, false);
        case 'assetLink':
            return wrapNodeIfNeeded(node, {
                type: 'link-to-matrix-asset',
                target: mark.attrs.target,
                matrixIdentifier: mark.attrs.matrixIdentifier,
                matrixDomain: mark.attrs.matrixDomain,
                matrixAssetId: mark.attrs.matrixAssetId,
                children: [],
            });
    }
    throw new Error(`Unsupported mark "${mark.type.name}" was applied to node.`);
};
/**
 * Converts Remirror node JSON structure to Squiz component JSON structure.
 * @param {ProsemirrorNode} node Remirror node to convert to component.
 * @returns {FormattedText} The converted Squiz component JSON.
 */
const remirrorNodeToSquizNode = (node) => {
    if (node?.type?.name !== 'doc') {
        throw new Error('Unable to convert from Remirror to Node data structure, unexpected node provided.');
    }
    // Squiz FormattedText should be set to undefined
    // To allow validation on required values
    if ((0, remirror_1.isDocNodeEmpty)(node) || node.childCount === 0) {
        return undefined;
    }
    const transformedFragment = transformFragment(node.content);
    return transformedFragment;
};
exports.remirrorNodeToSquizNode = remirrorNodeToSquizNode;
